// Rule-based parser: turns a dictated sentence (Hungarian or Serbian) into
// structured chart entries. It runs fully on the device (no network, no AI
// service). Everything language-specific lives in ./lexicon.
//
// When something is unclear it does not guess: it returns a Clarification
// with ready-made answers instead of a draft.

import type { TFunction } from 'i18next';
import { i18n, type Language } from '@/i18n';
import {
  type Code,
  isValidTooth,
  MATERIAL_CODES,
  type Material,
  normalizeSurfaces,
  SURFACE_CODES,
  type Surface,
  toothRange,
} from '@/utils/dental';
import {
  type Command,
  DIRECT_MATERIALS,
  fold,
  type Lexicon,
  lexiconFor,
  SURFACE_LETTERS,
  type Token,
} from './lexicon';

// ── Public types ─────────────────────────────────────────────

export type Draft = {
  tooth: number;
  surfaces: Surface[];
  code: Code;
  material: Material | null;
  note: string | null;
  /** 0..1 — how sure the parser is (times the speech engine's confidence). */
  confidence: number;
  /** The sentence the entry came from. */
  transcript: string;
};

export type ClarificationOption = { label: string; drafts: Draft[] };

export type Clarification = {
  id: string;
  question: string;
  transcript: string;
  /** Ready-made answers. Empty when only manual entry can resolve it. */
  options: ClarificationOption[];
  /**
   * "missing": a tooth was named but nothing said about it. A later sentence
   * that records something for these teeth answers it.
   */
  kind?: 'missing';
  teeth?: number[];
};

export type { Command };

export type ParseContext = {
  /** Teeth of the last entry made — used when a sentence names no tooth. */
  lastTeeth: number[] | null;
  /** A tooth was named but nothing said about it yet ("16-os…"). */
  awaitingTeeth: number[] | null;
  awaitingTranscript: string | null;
};

export type ParseResult = {
  drafts: Draft[];
  clarifications: Clarification[];
  commands: Command[];
  /** The visit's price in RSD, when one was said with its currency ("3000 dinara"). */
  price: number | null;
  context: ParseContext;
};

export const EMPTY_CONTEXT: ParseContext = {
  lastTeeth: null,
  awaitingTeeth: null,
  awaitingTranscript: null,
};

// ── Tokens and mentions ──────────────────────────────────────

type ToothMention =
  | { kind: 'teeth'; teeth: number[]; confidence: number; fromRange: boolean }
  | { kind: 'ambiguous'; candidates: number[]; label: string }
  | { kind: 'invalid'; value: number };

type Mention =
  | { type: 'tooth'; i: number; tooth: ToothMention; negated: boolean }
  | { type: 'code'; i: number; code: Code; negated: boolean; genericBridge: boolean }
  | { type: 'surfaces'; i: number; surfaces: Surface[] }
  | { type: 'material'; i: number; material: Material }
  | { type: 'planned'; i: number }
  | { type: 'grade'; i: number; grade: string }
  | { type: 'note'; i: number; text: string }
  | { type: 'correction'; i: number };

type Group = {
  teeth: ToothMention[];
  codes: Extract<Mention, { type: 'code' }>[];
  surfaces: Surface[];
  materials: Extract<Mention, { type: 'material' }>[];
  planned: boolean;
  grade: string | null;
  notes: string[];
  codesBeforeTeeth: boolean;
};

let clarificationSeq = 0;
function nextId(): string {
  clarificationSeq += 1;
  return `q${Date.now().toString(36)}${clarificationSeq}`;
}

/** Translated question texts (parser namespace) and dental labels (domain namespace). */
type Words = { q: TFunction; d: TFunction };

function wordsFor(lang: Language): Words {
  return { q: i18n.getFixedT(lang, 'parser'), d: i18n.getFixedT(lang, 'domain') };
}

const codeName = (w: Words, code: Code) => w.d(`codes.${code}`);
const materialName = (w: Words, material: Material) => w.d(`materials.${material}`);

function tokenize(text: string): Token[] {
  return text
    .replace(/[„"“”'’():]/g, ' ')
    .split(/[\s,]+/)
    .map((raw) => raw.replace(/^[-–]+|[-–.]+$/g, ''))
    .filter(Boolean)
    .map((raw) => ({ raw, norm: fold(raw) }));
}

/** Digits, with "." "," or a no-break space between thousands ("3.000", "12,500"); a plain space would join a tooth to the price. */
const PRICE_NUMBER = String.raw`\d{1,3}(?:[.,\u00a0\u202f]\d{3})+|\d+`;

/**
 * "3000 dinara", "3.000 RSD", "3 hiljade dinara": the price said with its
 * currency (the last one counts), and the text without it. Read before the
 * clauses are split, since "300.000" would break at the dot.
 */
function readPrice(text: string, lex: Lexicon): { price: number | null; rest: string } {
  const pattern = new RegExp(
    String.raw`(?<![\p{L}\d])(${PRICE_NUMBER})\s*(${lex.price.thousand})?\s*(?:${lex.price.unit})(?![\p{L}\d])`,
    'giu'
  );
  let price: number | null = null;
  const rest = text.replace(pattern, (_, digits: string, thousand?: string) => {
    const value = Number(digits.replace(/\D/g, '')) * (thousand ? 1000 : 1);
    if (value > 0 && value < 1e9) price = value;
    return ' ';
  });
  return { price, rest };
}

/** Split into clauses on sentence punctuation and on "majd", "zatim"… */
function splitClauses(text: string, lex: Lexicon): string[] {
  const sentences = text.split(/(?<!\b\d{1,2})[.;!?]+|\n+/);
  const out: string[] = [];
  for (const s of sentences) {
    let current: string[] = [];
    for (const word of s.split(/\s+/)) {
      if (lex.separatorWords.has(fold(word).replace(/[,]/g, ''))) {
        if (current.length) out.push(current.join(' '));
        current = [];
      } else if (word) {
        current.push(word);
      }
    }
    if (current.length) out.push(current.join(' '));
  }
  return out.map((s) => s.trim()).filter(Boolean);
}

// ── Word matching ────────────────────────────────────────────

function matchCode(lex: Lexicon, norm: string): { code: Code; genericBridge: boolean } | null {
  for (const rule of lex.codeRules) {
    const hit = rule.exact?.includes(norm) || rule.stems?.some((st) => norm.startsWith(st));
    if (hit) return { code: rule.code, genericBridge: !!rule.genericBridge };
  }
  return null;
}

function matchMaterial(lex: Lexicon, norm: string): Material | null {
  return lex.materialRules.find((r) => r.stems.some((st) => norm.startsWith(st)))?.material ?? null;
}

function matchSurfaces(lex: Lexicon, token: Token): Surface[] | null {
  const { raw, norm } = token;
  if (lex.excludedSurfaceAbbr.has(raw)) return null;
  if (/^[MODIBLPV]{1,5}$/.test(raw) || lex.lowercaseSurfaceAbbr.has(norm)) {
    return [...new Set([...raw.toUpperCase()].map((ch) => SURFACE_LETTERS[ch]))];
  }
  if (!lex.surfaceStems.some(([stem]) => norm.startsWith(stem))) return null;
  // Compound forms: "mezio-okkluzo-disztalis", "meziookkluzalis".
  const found = new Set<Surface>();
  let i = 0;
  while (i < norm.length) {
    const hit = lex.surfaceStems.find(([stem]) => norm.startsWith(stem, i));
    if (hit) found.add(hit[1]);
    i += hit ? hit[0].length : 1;
  }
  return found.size ? [...found] : null;
}

type Location = { upper?: boolean; right?: boolean; primary: boolean };

function locate(lex: Lexicon, words: string[]): Location {
  const loc: Location = { primary: false };
  for (const w of words) {
    const kind = lex.location(w);
    if (kind === 'upper' || kind === 'lower') loc.upper = kind === 'upper';
    if (kind === 'right' || kind === 'left') loc.right = kind === 'right';
    if (kind === 'primary') loc.primary = true;
  }
  return loc;
}

function positionCandidates(position: number, loc: Location): number[] {
  const [ur, ul, ll, lr] = loc.primary ? [5, 6, 7, 8] : [1, 2, 3, 4];
  return [ur, ul, ll, lr]
    .filter((q) => {
      const upper = q === ur || q === ul;
      const right = q === ur || q === lr;
      return (
        (loc.upper === undefined || loc.upper === upper) &&
        (loc.right === undefined || loc.right === right)
      );
    })
    .map((q) => q * 10 + position)
    .filter(isValidTooth);
}

// Each reader looks at the token at `i` and either claims it (returning the
// mentions it found and where to continue) or returns null for the next reader.
// `negate` in the result updates the pending "nem/nincs/ne"; omitted = unchanged.

type ReadState = { negate: boolean; lex: Lexicon };
type Read = { mentions: Mention[]; next: number; negate?: boolean };
type Reader = (tokens: Token[], i: number, s: ReadState) => Read | null;

function toothRead(i: number, s: ReadState, tooth: ToothMention, next: number): Read {
  return { mentions: [{ type: 'tooth', i, tooth, negated: s.negate }], next, negate: false };
}

function rangeRead(i: number, s: ReadState, from: number, to: number, next: number): Read {
  const range = toothRange(from, to);
  const tooth: ToothMention = range
    ? { kind: 'teeth', teeth: range, confidence: 0.95, fromRange: true }
    : { kind: 'invalid', value: from };
  return toothRead(i, s, tooth, next);
}

/** Range written as one token: "13-23", "13-23-ig". */
const readRangeToken: Reader = (tokens, i, s) => {
  const m = /^(\d{2})-(\d{2})(?:-?[a-z]+)?$/.exec(tokens[i].norm);
  return m && toothRange(Number(m[1]), Number(m[2]))
    ? rangeRead(i, s, Number(m[1]), Number(m[2]), i + 1)
    : null;
};

/** "egy-hatos", "jedan-šest" as one token. */
const readHyphenPair: Reader = (tokens, i, s) => {
  const parts = tokens[i].norm.split('-');
  const value = parts.length === 2 ? s.lex.readQuadrantPair(parts[0], parts[1]) : null;
  return value
    ? toothRead(i, s, { kind: 'teeth', teeth: [value], confidence: 0.95, fromRange: false }, i + 1)
    : null;
};

/**
 * One tooth for a range end: a number ("13", "trinaestica"), an FDI pair
 * ("jedan-tri", "jedan tri") or a position with a side ("gornje desne trojke").
 */
function readRangeEnd(
  tokens: Token[],
  i: number,
  lex: Lexicon
): { value: number; used: number } | null {
  const num = lex.readNumber(tokens, i);
  if (num) return num;
  const parts = tokens[i].norm.split('-');
  const hyphen = parts.length === 2 ? lex.readQuadrantPair(parts[0], parts[1]) : null;
  if (hyphen) return { value: hyphen, used: 1 };
  const spaced = tokens[i + 1] ? lex.readQuadrantPair(tokens[i].norm, tokens[i + 1].norm) : null;
  if (spaced) return { value: spaced, used: 2 };
  const words: string[] = [];
  const j = collectLocationWords(lex, tokens, i, 4, words);
  const found = positionAt(lex, tokens, j, hasSide(lex, words));
  const candidates = found ? positionCandidates(found.position, locate(lex, words)) : [];
  return found && candidates.length === 1
    ? { value: candidates[0], used: j - i + found.used }
    : null;
}

/** "od 13 do 23", "od jedan-tri do dva-tri" (Serbian prepositions). */
const readPrepositionRange: Reader = (tokens, i, s) => {
  if (!(s.lex.rangeFrom.has(tokens[i].norm) && tokens[i + 1])) return null;
  const start = readRangeEnd(tokens, i + 1, s.lex);
  if (!start) return null;
  const j = i + 1 + start.used;
  if (!(tokens[j] && s.lex.rangeTo.has(tokens[j].norm) && tokens[j + 1])) return null;
  const end = readRangeEnd(tokens, j + 1, s.lex);
  return end ? rangeRead(i, s, start.value, end.value, j + 1 + end.used) : null;
};

/** "16-tól 23-ig", "13-tól a 23-ig" (Hungarian suffixes). */
function readSuffixRange(
  tokens: Token[],
  i: number,
  s: ReadState,
  used: number,
  from: number
): Read | null {
  let j = i + used;
  while (tokens[j] && (tokens[j].norm === 'a' || tokens[j].norm === 'az')) j += 1;
  const end = tokens[j] ? s.lex.readNumber(tokens, j) : null;
  return end?.to ? rangeRead(i, s, from, end.value, j + end.used) : null;
}

/** A full tooth number: "16", "16-os", "tizenhatoson", "šesnaestica", "dvadeset šest". */
const readNumberMention: Reader = (tokens, i, s) => {
  const num = s.lex.readNumber(tokens, i);
  if (!num) return null;
  const range = num.from ? readSuffixRange(tokens, i, s, num.used, num.value) : null;
  if (range) return range;
  const next = i + num.used;
  if (isValidTooth(num.value)) {
    return toothRead(
      i,
      s,
      { kind: 'teeth', teeth: [num.value], confidence: 1, fromRange: false },
      next
    );
  }
  // Only call it a wrong tooth number when it was clearly meant as one.
  // ("19-es", "19 fog", "19 kariesz" — but not "20 éve").
  const after = tokens[next];
  const nextWord = after?.norm ?? '';
  const meantAsTooth =
    num.adjectival ||
    TOOTH_WORDS.some((w) => nextWord.startsWith(w)) ||
    (!!after && (!!matchCode(s.lex, nextWord) || !!matchSurfaces(s.lex, after)));
  return meantAsTooth ? toothRead(i, s, { kind: 'invalid', value: num.value }, next) : null;
};

/** "egy hatos", "tri šest" as two tokens (less certain: "egy" is also the article). */
const readSpacedPair: Reader = (tokens, i, s) => {
  const next = tokens[i + 1];
  const value = next ? s.lex.readQuadrantPair(tokens[i].norm, next.norm) : null;
  return value
    ? toothRead(i, s, { kind: 'teeth', teeth: [value], confidence: 0.8, fromRange: false }, i + 2)
    : null;
};

function collectLocationWords(
  lex: Lexicon,
  tokens: Token[],
  from: number,
  max: number,
  words: string[]
): number {
  let j = from;
  while (tokens[j] && lex.location(tokens[j].norm) && j - from < max) {
    words.push(tokens[j].norm);
    j += 1;
  }
  return j;
}

/** "16 fog", "16 zub", "16 tooth", "16 tann": the number was meant as a tooth. */
const TOOTH_WORDS = ['fog', 'zub', 'tooth', 'teeth', 'tann', 'tenn'];

/** A tooth position at `j` (one or more words), using the language's own reader if it has one. */
function positionAt(
  lex: Lexicon,
  tokens: Token[],
  j: number,
  afterLocation: boolean
): { position: number; used: number } | null {
  if (!tokens[j]) return null;
  if (lex.readPositionAt) return lex.readPositionAt(tokens, j, afterLocation);
  const position = lex.readPosition(tokens[j].norm);
  return position === null ? null : { position, used: 1 };
}

/** Real side words ("felső", "upper"), not fillers like "a" / "the". */
function hasSide(lex: Lexicon, words: string[]): boolean {
  return words.some((w) => {
    const kind = lex.location(w);
    return kind !== null && kind !== 'filler';
  });
}

/** "felső jobb hatos", "gornja desna šestica", "upper right first molar", "hatos". */
const readLocation: Reader = (tokens, i, s) => {
  const { lex } = s;
  const n = tokens[i].norm;
  if (!(lex.location(n) || positionAt(lex, tokens, i, false))) return null;
  const words: string[] = [];
  const j = collectLocationWords(lex, tokens, i, 4, words);
  const found = positionAt(lex, tokens, j, hasSide(lex, words));
  if (!found) return null;
  const k = collectLocationWords(lex, tokens, j + found.used, 3, words);
  const candidates = positionCandidates(found.position, locate(lex, words));
  if (candidates.length === 0) return { mentions: [], next: k };
  if (candidates.length === 1) {
    return toothRead(
      i,
      s,
      { kind: 'teeth', teeth: candidates, confidence: 0.95, fromRange: false },
      k
    );
  }
  const label = tokens
    .slice(i, k)
    .map((x) => x.raw)
    .join(' ');
  return toothRead(i, s, { kind: 'ambiguous', candidates, label }, k);
};

/** Fixed phrases and control words. */
const readPhrase: Reader = (tokens, i, s) => {
  const n = tokens[i].norm;
  const phrase = s.lex.readPhrase(tokens, i);
  if (phrase?.kind === 'code') {
    const code: Mention = {
      type: 'code',
      i,
      code: phrase.code,
      negated: false,
      genericBridge: false,
    };
    return { mentions: [code], next: i + phrase.used, negate: false };
  }
  if (phrase?.kind === 'material') {
    return {
      mentions: [{ type: 'material', i, material: phrase.material }],
      next: i + phrase.used,
    };
  }
  if (s.lex.correctionWords.has(n))
    return { mentions: [{ type: 'correction', i }], next: i + 1, negate: false };
  if (s.lex.negationWords.has(n)) return { mentions: [], next: i + 1, negate: true };
  return null;
};

const readGrade: Reader = (tokens, i, s) => {
  const grade = s.lex.readGrade(tokens, i);
  return grade
    ? { mentions: [{ type: 'grade', i, grade: grade.grade }], next: i + grade.used }
    : null;
};

const readSurfaceMention: Reader = (tokens, i, s) => {
  const surfaces = matchSurfaces(s.lex, tokens[i]);
  return surfaces ? { mentions: [{ type: 'surfaces', i, surfaces }], next: i + 1 } : null;
};

const readCodeMention: Reader = (tokens, i, s) => {
  const n = tokens[i].norm;
  const planned = s.lex.isPlanned(n);
  const code = matchCode(s.lex, n);
  const mentions: Mention[] = [];
  if (code) mentions.push({ type: 'code', i, ...code, negated: s.negate });
  if (planned) mentions.push({ type: 'planned', i });
  if (!mentions.length) return null;
  return { mentions, next: i + 1, negate: code ? false : undefined };
};

const readMaterialMention: Reader = (tokens, i, s) => {
  const material = matchMaterial(s.lex, tokens[i].norm);
  return material ? { mentions: [{ type: 'material', i, material }], next: i + 1 } : null;
};

/** Notes, filler, and unknown words (an unknown word ends a pending negation). */
const readRest: Reader = (tokens, i, s) => {
  const n = tokens[i].norm;
  const note = s.lex.note(n);
  if (note) return { mentions: [{ type: 'note', i, text: note }], next: i + 1 };
  return { mentions: [], next: i + 1, negate: s.lex.fillerWords.has(n) ? undefined : false };
};

const READERS: Reader[] = [
  readRangeToken,
  readHyphenPair,
  readPrepositionRange,
  readNumberMention,
  readSpacedPair,
  readLocation,
  readPhrase,
  readGrade,
  readSurfaceMention,
  readCodeMention,
  readMaterialMention,
  readRest,
];

/** Let the language split written compounds ("amalgamfylling") into words first. */
function splitCompounds(tokens: Token[], lex: Lexicon): Token[] {
  if (!lex.splitCompound) return tokens;
  return tokens.flatMap((t) => {
    const parts = lex.splitCompound?.(t.norm);
    return parts ? parts.map((norm) => ({ raw: norm, norm })) : [t];
  });
}

function extractMentions(rawTokens: Token[], lex: Lexicon): Mention[] {
  const tokens = splitCompounds(rawTokens, lex);
  const mentions: Mention[] = [];
  const state: ReadState = { negate: false, lex };
  let i = 0;
  while (i < tokens.length) {
    let read: Read | null = null;
    for (const reader of READERS) {
      read = reader(tokens, i, state);
      if (read) break;
    }
    if (!read) break;
    mentions.push(...read.mentions);
    if (read.negate !== undefined) state.negate = read.negate;
    i = read.next;
  }
  return mentions;
}

/** "nem a 16-os, hanem a 26-os" → drop the 16. */
function applyCorrections(mentions: Mention[]): Mention[] {
  const out: Mention[] = [];
  for (const m of mentions) {
    if (m.type === 'correction') {
      for (let k = out.length - 1; k >= 0; k -= 1) {
        const prev = out[k];
        if (prev.type === 'tooth' && prev.negated) {
          out.splice(k, 1);
          break;
        }
      }
      continue;
    }
    out.push(m);
  }
  return out;
}

// ── Grouping ─────────────────────────────────────────────────

function emptyGroup(): Group {
  return {
    teeth: [],
    codes: [],
    surfaces: [],
    materials: [],
    planned: false,
    grade: null,
    notes: [],
    codesBeforeTeeth: false,
  };
}

function hasContent(g: Group): boolean {
  return g.codes.length > 0 || g.surfaces.length > 0 || g.materials.length > 0 || g.grade !== null;
}

type ContentMention = Exclude<Mention, { type: 'tooth' | 'correction' }>;

function pushUnique<T>(list: T[], items: T[]) {
  for (const item of items) if (!list.includes(item)) list.push(item);
}

function addContent(g: Group, m: ContentMention) {
  if (m.type === 'code') g.codes.push(m);
  else if (m.type === 'surfaces') pushUnique(g.surfaces, m.surfaces);
  else if (m.type === 'material') g.materials.push(m);
  else if (m.type === 'planned') g.planned = true;
  else if (m.type === 'grade') g.grade = m.grade;
  else pushUnique(g.notes, [m.text]);
}

/** A new tooth after a finding starts a new group; a list of teeth shares one. */
function groupMentions(mentions: Mention[]): Group[] {
  const groups: Group[] = [];
  let cur = emptyGroup();
  for (const m of mentions) {
    if (m.type === 'correction') continue;
    if (m.type !== 'tooth') {
      addContent(cur, m);
      continue;
    }
    if (cur.teeth.length && hasContent(cur)) {
      groups.push(cur);
      cur = emptyGroup();
    }
    if (!cur.teeth.length && hasContent(cur)) cur.codesBeforeTeeth = true;
    cur.teeth.push(m.tooth);
  }
  if (cur.teeth.length || hasContent(cur)) groups.push(cur);
  return groups;
}

// ── Drafts ───────────────────────────────────────────────────

const toothLabel = (teeth: number[]) =>
  teeth.length > 1 ? `${teeth[0]}–${teeth[teeth.length - 1]}` : `${teeth[0]}`;

function round(n: number): number {
  return Math.round(Math.max(0, Math.min(1, n)) * 100) / 100;
}

function noteFor(w: Words, g: Group, code: Code, extra?: string): string | null {
  const parts: string[] = [];
  if (g.planned) parts.push(w.d('planned'));
  if (code === 'mobility' && g.grade) parts.push(w.d('grade', { grade: g.grade }));
  parts.push(...g.notes);
  if (extra) parts.push(extra);
  return parts.length ? parts.join(' · ') : null;
}

function nearestMaterial(g: Group, at: number): Material | null {
  let best: { m: Material; d: number } | null = null;
  for (const mm of g.materials) {
    const d = Math.abs(mm.i - at);
    if (!best || d < best.d) best = { m: mm.material, d };
  }
  return best?.m ?? null;
}

type CodeSpec = { code: Code; material: Material | null; at: number; genericBridge: boolean };

/** A plain "híd" over a range: abutments at both ends, pontics between. */
function bridgeCode(spec: CodeSpec, index: number, count: number, fromRange: boolean): Code {
  if (!(spec.genericBridge && fromRange) || count < 3) return spec.code;
  return index === 0 || index === count - 1 ? 'bridge_abutment' : 'bridge_pontic';
}

function draftsFor(
  w: Words,
  teeth: number[],
  fromRange: boolean,
  specs: CodeSpec[],
  g: Group,
  confidence: number,
  transcript: string,
  extraNote?: string
): Draft[] {
  const drafts: Draft[] = [];
  const seen = new Set<string>();
  teeth.forEach((tooth, index) => {
    for (const spec of specs) {
      const code = bridgeCode(spec, index, teeth.length, fromRange);
      const key = `${tooth}:${code}`;
      if (seen.has(key)) continue;
      seen.add(key);
      drafts.push({
        tooth,
        code,
        surfaces: SURFACE_CODES.has(code) ? normalizeSurfaces(tooth, g.surfaces) : [],
        material: MATERIAL_CODES.has(code) ? spec.material : null,
        note: noteFor(w, g, code, extraNote),
        confidence: round(confidence),
        transcript,
      });
    }
  });
  return drafts;
}

/** Cannot be said of a tooth that is missing or being extracted in the same breath. */
const CONFLICTS_WITH_REMOVAL: ReadonlySet<Code> = new Set([
  'caries',
  'filling',
  'inlay',
  'sealant',
  'crown',
  'veneer',
  'root_canal',
  'mobility',
  'fracture',
]);

/**
 * Clash with "missing" only. With an extraction they are a sequence:
 * "extraction and implant" (immediate implant), "root remnant extracted".
 */
const CONFLICTS_WITH_MISSING_ONLY: ReadonlySet<Code> = new Set([
  'root_remnant',
  'devital',
  // "the bridge / implant fell out": the restoration, not the tooth.
  'bridge_abutment',
  'bridge_pontic',
  'implant',
]);

type Resolution =
  | { kind: 'drafts'; specs: CodeSpec[]; confidence: number; extraNote?: string }
  | { kind: 'ask'; question: string; options: { label: string; specs: CodeSpec[] }[] };

/** Decide which codes a group stands for, or what to ask. */
function resolveCodes(w: Words, g: Group, teethLabel: string): Resolution | null {
  const positive = g.codes.filter((c) => !c.negated);
  const specs: CodeSpec[] = positive.map((c) => ({
    code: c.code,
    material: MATERIAL_CODES.has(c.code) ? nearestMaterial(g, c.i) : null,
    at: c.i,
    genericBridge: c.genericBridge,
  }));

  if (specs.length) {
    const removal = specs.find(
      (s) => (s.code === 'missing' || s.code === 'extraction') && !g.planned
    );
    const clash = removal
      ? specs.filter(
          (s) =>
            CONFLICTS_WITH_REMOVAL.has(s.code) ||
            (removal.code === 'missing' && CONFLICTS_WITH_MISSING_ONLY.has(s.code))
        )
      : [];
    if (removal && clash.length) {
      const all = [removal, ...clash];
      return {
        kind: 'ask',
        question: w.q('conflict', {
          teeth: teethLabel,
          removal: codeName(w, removal.code).toLowerCase(),
          others: clash.map((c) => codeName(w, c.code).toLowerCase()).join(', '),
        }),
        options: all.map((s) => ({ label: codeName(w, s.code), specs: [s] })),
      };
    }
    return { kind: 'drafts', specs, confidence: 1 };
  }

  const negated = g.codes.filter((c) => c.negated);
  if (negated.length) {
    const what = negated.map((c) => codeName(w, c.code).toLowerCase()).join(', ');
    return {
      kind: 'drafts',
      specs: [{ code: 'sound', material: null, at: negated[0].i, genericBridge: false }],
      confidence: 0.9,
      extraNote: w.q('noFinding', { what }),
    };
  }

  if (g.materials.length) {
    const material = g.materials[0].material;
    if (DIRECT_MATERIALS.has(material)) {
      return {
        kind: 'drafts',
        specs: [{ code: 'filling', material, at: g.materials[0].i, genericBridge: false }],
        confidence: 0.85,
      };
    }
    const codes: Code[] = ['crown', 'inlay', 'veneer'];
    return {
      kind: 'ask',
      question: w.q('materialWhat', { material: materialName(w, material), teeth: teethLabel }),
      options: codes.map((code) => ({
        label: w.q('optionWithMaterial', {
          code: codeName(w, code),
          material: materialName(w, material).toLowerCase(),
        }),
        specs: [{ code, material, at: 0, genericBridge: false }],
      })),
    };
  }

  if (g.surfaces.length) {
    const codes: Code[] = ['caries', 'filling'];
    return {
      kind: 'ask',
      question: w.q('surfaceWhat', { teeth: teethLabel, surfaces: g.surfaces.join('') }),
      options: codes.map((code) => ({
        label: codeName(w, code),
        specs: [{ code, material: null, at: 0, genericBridge: false }],
      })),
    };
  }

  if (g.grade) {
    return {
      kind: 'drafts',
      specs: [{ code: 'mobility', material: null, at: 0, genericBridge: false }],
      confidence: 0.85,
    };
  }

  return null;
}

// ── Main entry point ─────────────────────────────────────────

type ClearTooth = Extract<ToothMention, { kind: 'teeth' }>;
type AmbiguousTooth = Extract<ToothMention, { kind: 'ambiguous' }>;

/** State for one parseDictation call. */
class Session {
  readonly result: ParseResult;
  private readonly speech: number;
  private readonly w: Words;

  constructor(
    private readonly lex: Lexicon,
    context: ParseContext,
    speechConfidence: number | undefined
  ) {
    this.w = wordsFor(lex.lang);
    this.speech = speechConfidence && speechConfidence > 0 ? Math.max(0.5, speechConfidence) : 1;
    this.result = {
      drafts: [],
      clarifications: [],
      commands: [],
      price: null,
      context: { ...context },
    };
  }

  private get ctx(): ParseContext {
    return this.result.context;
  }

  ask(question: string, transcript: string, options: ClarificationOption[] = []) {
    this.result.clarifications.push({ id: nextId(), question, transcript, options });
  }

  /** "Mit rögzítsek a 16 fogra?" — answered by any later entry for these teeth. */
  private askMissing(teeth: number[], transcript: string) {
    this.result.clarifications.push({
      id: nextId(),
      question: this.w.q('whatForTooth', { teeth: toothLabel(teeth) }),
      transcript,
      options: [],
      kind: 'missing',
      teeth,
    });
  }

  clause(clause: string) {
    const folded = fold(clause).trim();
    const command = this.lex.commandPatterns.find((c) => c.pattern.test(folded));
    if (command) {
      this.result.commands.push(command.command);
      return;
    }
    const mentions = applyCorrections(extractMentions(tokenize(clause), this.lex));
    // "minden más változatlan" / "ostalo je isto" alone adds nothing; with a finding
    // in the same clause ("16 karijes, ostalo je isto") the finding still counts.
    const saysSomething = mentions.some((m) => m.type === 'tooth' || m.type === 'code');
    if (this.lex.noChangePattern.test(folded) && !saysSomething) return;
    const groups = groupMentions(mentions);
    // "lower incisors worn": other teeth were meant, so the last tooth is no fallback.
    const namesPlace = tokenize(clause).some((t) => {
      const kind = this.lex.location(t.norm);
      return kind !== null && kind !== 'filler';
    });
    groups.forEach((g, gi) => {
      if (g.teeth.length) this.group(g, groups[gi - 1] ?? null, gi === groups.length - 1, clause);
      else this.toothless(g, clause, namesPlace);
    });
  }

  /** Findings without a tooth: complete a waiting tooth, or reuse the last one. */
  private toothless(g: Group, clause: string, namesPlace: boolean) {
    const { awaitingTeeth, awaitingTranscript, lastTeeth } = this.ctx;
    if (awaitingTeeth) {
      this.ctx.awaitingTeeth = null;
      this.ctx.awaitingTranscript = null;
      this.emit(awaitingTeeth, false, g, 0.9, `${awaitingTranscript ?? ''} ${clause}`.trim());
      return;
    }
    if (lastTeeth && !namesPlace) {
      this.emit(lastTeeth, false, g, 0.7, clause);
      return;
    }
    const what = g.codes.map((c) => codeName(this.w, c.code).toLowerCase()).join(', ');
    this.ask(what ? this.w.q('whichToothFor', { what }) : this.w.q('whichTooth'), clause);
  }

  /** A new tooth is named: an earlier tooth still waiting becomes a question — unless it is the same tooth again ("11 … 11 hiányzik"). */
  private flushAwaiting(next: number[]) {
    const { awaitingTeeth, awaitingTranscript } = this.ctx;
    if (!awaitingTeeth) return;
    if (!awaitingTeeth.every((t) => next.includes(t))) {
      this.askMissing(awaitingTeeth, awaitingTranscript ?? '');
    }
    this.ctx.awaitingTeeth = null;
    this.ctx.awaitingTranscript = null;
  }

  private group(g: Group, prev: Group | null, isLast: boolean, clause: string) {
    this.flushAwaiting(g.teeth.flatMap((t) => (t.kind === 'teeth' ? t.teeth : [])));
    for (const tm of g.teeth) {
      if (tm.kind === 'invalid') {
        this.ask(this.w.q('invalidTooth', { value: tm.value }), clause);
      }
    }
    const clear = g.teeth.filter((t): t is ClearTooth => t.kind === 'teeth');
    const ambiguous = g.teeth.filter((t): t is AmbiguousTooth => t.kind === 'ambiguous');

    // "kariesz a 16-oson és a 17-esen": the second tooth takes the first one's finding.
    const inherit = !hasContent(g) && !!prev?.codesBeforeTeeth && hasContent(prev);
    const group = inherit && prev ? { ...prev, teeth: g.teeth } : g;

    if (!hasContent(group)) {
      this.nothingSaid(clear, ambiguous, isLast, clause);
      return;
    }
    for (const tm of clear) {
      this.emit(tm.teeth, tm.fromRange, group, tm.confidence * (inherit ? 0.85 : 1), clause);
    }
    for (const am of ambiguous) this.whichTooth(am, group, clause);
  }

  /** Teeth named, nothing said about them (yet). */
  private nothingSaid(
    clear: ClearTooth[],
    ambiguous: AmbiguousTooth[],
    isLast: boolean,
    clause: string
  ) {
    if (ambiguous.length) {
      this.ask(this.w.q('whichOf', { label: ambiguous[0].label }), clause);
      return;
    }
    const teeth = clear.flatMap((t) => t.teeth);
    if (!teeth.length) return;
    if (isLast) {
      this.ctx.awaitingTeeth = teeth;
      this.ctx.awaitingTranscript = clause;
    } else {
      this.askMissing(teeth, clause);
    }
  }

  /** "hatos" without a quadrant: offer each candidate as a ready answer. */
  private whichTooth(am: AmbiguousTooth, group: Group, clause: string) {
    const resolution = resolveCodes(this.w, group, am.label);
    if (!resolution || resolution.kind === 'ask') {
      this.ask(this.w.q('whichOf', { label: am.label }), clause);
      return;
    }
    const confidence = 0.9 * resolution.confidence * this.speech;
    this.ask(
      this.w.q('whichOf', { label: am.label }),
      clause,
      am.candidates.map((tooth) => ({
        label: `${tooth}`,
        drafts: draftsFor(
          this.w,
          [tooth],
          false,
          resolution.specs,
          group,
          confidence,
          clause,
          resolution.extraNote
        ),
      }))
    );
  }

  private emit(
    teeth: number[],
    fromRange: boolean,
    g: Group,
    confidence: number,
    transcript: string
  ) {
    const resolution = resolveCodes(this.w, g, toothLabel(teeth));
    if (!resolution) return;
    if (resolution.kind === 'ask') {
      const c = 0.9 * confidence * this.speech;
      this.ask(
        resolution.question,
        transcript,
        resolution.options.map((o) => ({
          label: o.label,
          drafts: draftsFor(this.w, teeth, fromRange, o.specs, g, c, transcript),
        }))
      );
      return;
    }
    const drafts = draftsFor(
      this.w,
      teeth,
      fromRange,
      resolution.specs,
      g,
      confidence * resolution.confidence * this.speech,
      transcript,
      resolution.extraNote
    );
    this.result.drafts.push(...drafts);
    if (drafts.length) this.ctx.lastTeeth = teeth;
  }
}

/**
 * Parse one chunk of dictation. Pass the returned `context` into the next call
 * so a tooth named in one breath ("16-os…") can be completed in the next.
 */
export function parseDictation(
  text: string,
  context: ParseContext = EMPTY_CONTEXT,
  options: { speechConfidence?: number; lang?: Language } = {}
): ParseResult {
  const lex = lexiconFor(options.lang ?? 'hu');
  const session = new Session(lex, context, options.speechConfidence);
  const { price, rest } = readPrice(text, lex);
  session.result.price = price;
  for (const clause of splitClauses(rest, lex)) session.clause(clause);
  return session.result;
}

/** The price in a whole utterance, for speech that arrives in growing pieces ("4500", then "4500 dinara"). */
export function readSpokenPrice(text: string, lang: Language = 'hu'): number | null {
  return readPrice(text, lexiconFor(lang)).price;
}

/** Call when dictation stops: a tooth still waiting for its finding becomes a question. */
export function finishDictation(context: ParseContext, lang: Language = 'hu'): Clarification[] {
  if (!context.awaitingTeeth) return [];
  return [
    {
      id: nextId(),
      question: wordsFor(lang).q('whatForTooth', { teeth: toothLabel(context.awaitingTeeth) }),
      transcript: context.awaitingTranscript ?? '',
      options: [],
      kind: 'missing',
      teeth: context.awaitingTeeth,
    },
  ];
}
